import { DateTime } from 'luxon';
import { SubscriptionStatus } from '@cf-shared/data-model/couch-user';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';
import { resolveCoachLicenseMirror } from '@cf-shared/data-model/coach-license-mirror';
import type { CoachRailBlocks } from '@cf-shared/data-model/coach-license-mirror';
import {
  resolveCoachBillingRailState,
  resolveCoachNoticeKind,
  type CoachPaymentNoticeKind,
} from '@cf-shared/data-model/coach-billing-notice';

/**
 * The pure decision behind the coach's billing card on the home screen.
 *
 * Lives outside the component for the same reason `decideUpdateCardState` next
 * door does: a card-visibility rule with consequences should be unit-testable
 * without standing up Angular.
 *
 * ## One card, two things to say
 *
 * There used to be two cards here. #4393 Surface B warned a coach that their
 * clients were about to lose access, naming how many; #4455 added a second card
 * saying the coach's own payment had failed. Their conditions overlapped almost
 * completely, so a coach in dunning saw both, stacked, quoting the same date —
 * and chaining them so only one rendered silently retired the older one for
 * every coach on a single billing rail.
 *
 * So they are merged. This card states the money problem, and — only when it is
 * true — adds what it means for the coach's clients. That second sentence is
 * what carries #4393's client count forward.
 *
 * ## Three rules, and none of them may be collapsed into the others
 *
 * 1. **Does the coach have a money problem?** From `resolveCoachBillingRailState`
 *    plus `resolveCoachNoticeKind` — the SAME shared rule the emailed and pushed
 *    messages use, so a card can never claim a payment failed on a rail that
 *    cannot know that (the app stores map a deliberate cancel onto the same
 *    status as a declined card).
 * 2. **Do the coach's clients actually lose access?** From
 *    `resolveCoachLicenseMirror` (#4361), asked twice — see
 *    `clientsLoseAccessBecauseOfThis` below. This is NOT the same question as
 *    rule 1, and the gap between them is the whole reason it is asked
 *    separately: an admin-granted Legacy coach can have a failing tier
 *    subscription whose clients keep access regardless.
 * **The card shows on rule 1 alone.** Rule 2 only decides whether the clients
 * sentence is added. A coach with no clients still gets the card — seat #1 of a
 * Paddle coaching subscription is what pays for the coach's OWN Premium
 * membership, so a failed payment costs them their own access. Making the whole
 * card conditional on having clients is the single most likely way for a later
 * edit to break this, and it is pinned by a test.
 *
 * **How MANY clients there are is deliberately not asked here.** That count
 * lives in a watched document, and reading it registers a DocWatcher; this rule
 * returns a boolean so the component reads the roster only once the card is
 * going to render and has something to say about clients, rather than on every
 * home-screen draw for every user in the app.
 */
export interface CoachBillingCardInput {
  /**
   * The coach's own rail blocks. `CouchLoginResult` satisfies this shape
   * structurally, so the caller passes the login result straight in. Null when
   * signed out or offline.
   */
  coach: CoachRailBlocks | null | undefined;
  /** Evaluation time, passed in so the rule stays pure and testable. */
  now: DateTime;
  /**
   * Whether the coach has already dismissed the card in THIS app session.
   * Deliberately not persisted: a coach who dismisses once and then loses every
   * client a week later was not warned.
   */
  dismissedThisSession: boolean;
}

export interface CoachBillingCardDecision {
  /** Whether to render the card at all. Decided by the money problem alone. */
  show: boolean;
  /** Which of the three wordings. Only meaningful when `show` is true. */
  kind?: CoachPaymentNoticeKind;
  /** The date the plan runs to, as stored. Absent when there is none to show. */
  endDate?: string;
  /**
   * Whether this coach's clients actually lose access because of it.
   *
   * Deliberately a boolean rather than a count: the count comes from a watched
   * document, and reading it registers a DocWatcher. Returning the boolean lets
   * the caller read the roster ONLY once the card is going to render and has
   * something to say about clients, instead of on every home-screen draw for
   * every user in the app, coach or not.
   */
  clientsLoseAccess: boolean;
}

const NOT_SHOWING: CoachBillingCardDecision = { show: false, clientsLoseAccess: false };

/**
 * Coaches who have dismissed the card in this app session, by uid.
 *
 * Module state rather than storage, because the policy is PER SESSION: do not
 * nag a coach twice in one sitting, but do warn them again next time they open
 * the app while the problem is still running. Module state expresses exactly
 * that with nothing to clean up — it lives as long as the JavaScript context,
 * which IS one app session, and any full reload clears it. localStorage would
 * have needed a `local-storage-hygiene` allow-list entry and an expiry rule;
 * sessionStorage would have survived a sign-out on the same tab. Keyed by uid so
 * two accounts used in one session do not inherit each other's dismissal.
 *
 * Kept out of `decideCoachBillingCard`, which takes the answer as a plain
 * parameter and stays pure.
 */
const dismissedByUid = new Set<string>();

/** Record that this coach dismissed the card for the rest of this session. */
export function markCoachBillingCardDismissed(uid: string | undefined): void {
  if (uid) dismissedByUid.add(uid);
}

/** Whether this coach has already dismissed the card in this session. */
export function isCoachBillingCardDismissed(uid: string | undefined): boolean {
  return !!uid && dismissedByUid.has(uid);
}

/**
 * Whether this coach's clients actually lose access, now or when the named
 * block runs out.
 *
 * **This holds no precedence of its own — it asks Phase 1's, twice.** Whether a
 * coach's clients keep access is decided by exactly one function,
 * `resolveCoachLicenseMirror` (#4361), which takes a `now` for precisely this
 * purpose. The question is the difference between two evaluations: does the
 * rail grant now, and does it still grant one millisecond past this block's
 * expiry?
 *
 * Two cases fall out of that for free, and hand-written versions of this rule
 * got both wrong across three review cycles on #4393:
 *
 * - an admin-granted **Legacy** coach with a lapsing tier — the mirror answers
 *   `no-rail`, so their clients are restored to a permanent grant and lose
 *   nothing, however unhealthy the tier looks;
 * - a coach **mid-migration**, holding a lapsing tier alongside an active
 *   Paddle seat — the second call mirrors the live seat.
 *
 * Never reason from `resolveCoachCapability` instead. It answers a different
 * question ("may this coach add a client right now?"), its order differs in two
 * places, and it has no lapsed-block branch at all. See
 * `design-docs/coach-subscription-lapse.md`.
 */
function clientsLoseAccessBecauseOfThis(coach: CoachRailBlocks, now: DateTime): boolean {
  const atNow = resolveCoachLicenseMirror(coach, now);

  // `no-rail` means there is no subscription that CAN lapse — a Legacy coach.
  // Their clients hold a permanent grant that this card's money problem does
  // not touch, so nothing may be claimed on their behalf.
  if (atNow.kind === 'no-rail') return false;

  const grantsNow = atNow.kind === 'mirror'
    && isInGrace(atNow.subscriptionStatus, atNow.expiresDate_utc, now.toMillis());
  // Already gone. This is the paused / ended case, which the old lapse rule
  // could not reach at all because it only ever spoke about a FUTURE loss.
  if (!grantsNow) return true;

  // Still granting, so the question is whether it survives the expiry of the
  // block the MIRROR chose — which is not always the block this card is
  // reporting on. The two selectors differ: the mirror checks tier before seat,
  // while the card's rail state prefers the seat among equally-granting
  // candidates. Asking about the card's block instead would answer a question
  // about the wrong subscription and quietly drop the clients sentence for a
  // coach whose clients really do lose access.
  //
  // With no date there is nothing to evaluate against, and claiming a loss we
  // cannot place in time would be worse than saying nothing.
  const mirroredExpiry = atNow.kind === 'mirror' ? atNow.expiresDate_utc : undefined;
  if (!mirroredExpiry) return false;

  const justAfterExpiry = DateTime.fromISO(mirroredExpiry).plus({ milliseconds: 1 });
  const atAfter = resolveCoachLicenseMirror(coach, justAfterExpiry);
  const stillGrantsAfterwards =
    atAfter.kind === 'no-rail'
    || (atAfter.kind === 'mirror'
      && isInGrace(atAfter.subscriptionStatus, atAfter.expiresDate_utc, justAfterExpiry.toMillis()));

  return !stillGrantsAfterwards;
}

/** Whether to show the coach the billing card, in which wording, and whether to
 *  name their clients in it. */
export function decideCoachBillingCard(input: CoachBillingCardInput): CoachBillingCardDecision {
  if (input.dismissedThisSession) return NOT_SHOWING;
  if (!input.coach) return NOT_SHOWING;

  const state = resolveCoachBillingRailState(input.coach, input.now.toMillis());
  // No coaching subscription that can fail — a legacy admin-granted coach with
  // no rail at all, or somebody who is not a coach.
  if (!state) return NOT_SHOWING;

  // A healthy plan says nothing. This mirrors the sent message's own rule: only
  // Suspended, Paused and Terminated are worth speaking about, and the statuses
  // left out (a checkout in progress, or no plan at all) would otherwise
  // produce a card about nothing.
  if (
    state.subscriptionStatus !== SubscriptionStatus.Suspended
    && state.subscriptionStatus !== SubscriptionStatus.Paused
    && state.subscriptionStatus !== SubscriptionStatus.Terminated
  ) {
    return NOT_SHOWING;
  }

  const endDate = state.expiresDate_utc;
  // A granting-looking status whose expiry is already spent. Passed to the
  // wording rule so the card says the plan has STOPPED rather than quoting that
  // past date as a future one — see resolveCoachNoticeKind.
  const accessAlreadyEnded = !isInGrace(state.subscriptionStatus, endDate, input.now.toMillis());

  return {
    show: true,
    // `hasScheduledCancel` is false for the same reason the server passes false:
    // on the Paddle coach rail `Suspended` is written only by `past_due`, and
    // the coach-seat block carries no scheduled-change field yet (#4416 adds
    // one). See `server/functions/src/helpers/coach-billing-notify.ts`.
    kind: resolveCoachNoticeKind(state.rail, state.subscriptionStatus, false, accessAlreadyEnded),
    endDate,
    clientsLoseAccess: clientsLoseAccessBecauseOfThis(input.coach, input.now),
  };
}
